import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@shared/lib/cn';
import { Button } from '@shared/ui/button';
import { ToggleButton } from '@shared/ui/toggle-button';

import {
  GAP_CLASS,
  ITEM_WIDTH_CLASS,
  SELECT_ALL_BUTTON_SIZE,
  type ToggleGroupGap,
  type ToggleGroupItemWidth,
  type ToggleGroupSize,
} from './toggle-group.constant';
import { TOGGLE_GROUP_TEXT } from './toggle-group.text';

export interface ToggleGroupItem<V extends string | number> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

export interface ToggleGroupProps<V extends string | number> extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'onChange' | 'title'
> {
  /** 머리말 제목(heading-4). 없고 `selectAll` 도 없으면 머리말을 그리지 않는다 */
  title?: ReactNode;
  /** 제목 태그. 기본 h3 */
  titleAs?: 'h2' | 'h3' | 'h4' | 'p';
  items: readonly ToggleGroupItem<V>[];
  /** 고른 값 목록(single 이면 0~1개) */
  value: readonly V[];
  onChange: (value: V[]) => void;
  /** single = 하나만(radiogroup), multiple = 여럿(aria-pressed). 기본 multiple */
  selection?: 'single' | 'multiple';
  /** 머리말 오른쪽 "전체 선택 / 전체 해제". multiple 에서만. 기본 true */
  selectAll?: boolean;
  /** 기본 sm */
  size?: ToggleGroupSize;
  /** 토글 버튼 너비. 기본 hug */
  itemWidth?: ToggleGroupItemWidth;
  /** 머리말 줄과 버튼 목록 사이 간격: none 0 · sm 10 · md 16 · lg 20. 기본 md */
  gap?: ToggleGroupGap;
  /** 머리말 줄에 더할 클래스(높이 · 아래 선 등은 쓰는 쪽이 정한다) */
  headerClassName?: string;
  /** 버튼 목록에 더할 클래스(여백 등) */
  listClassName?: string;
}

/** 버튼 글자: 한 줄, 넘치면 말줄임(…) — 버튼 크기가 일정하다(전체 이름은 title). 2026-10-07 사용자(DD-70) */
const labelOf = (label: ReactNode) => <span className="min-w-0 truncate">{label}</span>;

/**
 * 토글 버튼 묶음 + 전체 선택 / 전체 해제(open-questions B9 — FundPicker · EvaluationItemPicker ·
 * 수동 메일의 펀드 선택이 같은 구조). 자료를 모른다: 선택지는 `items` 로 받는다.
 */
export function ToggleGroup<V extends string | number>({
  title,
  titleAs: Heading = 'h3',
  items,
  value,
  onChange,
  selection = 'multiple',
  selectAll = true,
  size = 'sm',
  itemWidth = 'hug',
  gap = 'md',
  headerClassName,
  listClassName,
  className,
  ...rest
}: ToggleGroupProps<V>) {
  const single = selection === 'single';
  const showSelectAll = !single && selectAll;
  const allSelected = items.length > 0 && items.every((item) => value.includes(item.value));

  function toggle(itemValue: V) {
    if (single) {
      if (!value.includes(itemValue)) onChange([itemValue]);
      return;
    }
    onChange(
      value.includes(itemValue)
        ? value.filter((selected) => selected !== itemValue)
        : [...value, itemValue],
    );
  }

  const hasHeader = title !== undefined || showSelectAll;

  return (
    <div {...rest} className={cn('flex flex-col', GAP_CLASS[gap], className)}>
      {hasHeader ? (
        <div className={cn('flex items-center justify-between gap-4', headerClassName)}>
          {title !== undefined ? (
            <Heading className="min-w-0 typo-heading-4 text-fg-default">{title}</Heading>
          ) : (
            <span />
          )}
          {showSelectAll ? (
            <Button
              variant="outline"
              size={SELECT_ALL_BUTTON_SIZE[size]}
              className="w-(--size-button-md)"
              onClick={() => onChange(allSelected ? [] : items.map((item) => item.value))}
            >
              {allSelected ? TOGGLE_GROUP_TEXT.clearAll : TOGGLE_GROUP_TEXT.selectAll}
            </Button>
          ) : null}
        </div>
      ) : null}
      <div
        role={single ? 'radiogroup' : 'group'}
        aria-label={typeof title === 'string' ? title : undefined}
        className={cn('flex flex-wrap gap-2-5', listClassName)}
      >
        {items.map((item) => {
          const on = value.includes(item.value);
          return single ? (
            <ToggleButton
              key={item.value}
              role="radio"
              aria-checked={on}
              aria-pressed={undefined}
              pressed={on}
              size={size}
              disabled={item.disabled}
              className={ITEM_WIDTH_CLASS[itemWidth]}
              onPressedChange={() => toggle(item.value)}
              title={typeof item.label === 'string' ? item.label : undefined}
            >
              {labelOf(item.label)}
            </ToggleButton>
          ) : (
            <ToggleButton
              key={item.value}
              pressed={on}
              size={size}
              disabled={item.disabled}
              className={ITEM_WIDTH_CLASS[itemWidth]}
              onPressedChange={() => toggle(item.value)}
              title={typeof item.label === 'string' ? item.label : undefined}
            >
              {labelOf(item.label)}
            </ToggleButton>
          );
        })}
      </div>
    </div>
  );
}
